// Session 27: the course registry.
//
// Course CONTENT lives here in the repo, not in the database, so it is
// reviewable and diffable in git. The database holds only what a person did
// (course_progress, course_responses, course_prompt_cards).
//
// Spine versus skin (the pattern carried over from the tennis course): a course
// object is the spine. Examples and vocabulary that change per audience live in
// the course's `skin`, so a customised edition for a white-glove client is a
// different skin over the same spine, not a fork. See the reuse report in
// Agency Brain > 03 Apps.
//
// A lesson is a plain object of typed blocks, rendered by
// components/course/LessonRenderer.jsx. Every lesson ends with the "What's
// missing?" box and a hidden video slot; the renderer adds both, so no lesson
// file has to repeat them.

import aiAtWork101 from './ai-at-work-101/index.js';

export const COURSES = [aiAtWork101];

export function courseByKey(key) {
  return COURSES.find((c) => c.key === key) || null;
}

export function lessonByKey(course, lessonKey) {
  if (!course) return null;
  return course.lessons.find((l) => l.key === lessonKey) || null;
}

// Lessons a reader can actually open. A lesson still being written is listed on
// the course page as "Coming soon" but has no page behind it.
export function readyLessons(course) {
  return course ? course.lessons.filter((l) => l.status === 'ready') : [];
}

// In Preview the course page shows the outcomes, the full lesson list and ONE
// free sample lesson. Everything else is locked.
//
// The course declares which lesson it wants as the sample, but a lesson that
// has not been written yet cannot be the sample or Preview would have nothing
// to open. So: the declared lesson if it is ready, otherwise the first ready
// lesson. This self-corrects as lessons are approved, with no code change.
export function sampleLesson(course) {
  const ready = readyLessons(course);
  if (!ready.length) return null;
  return ready.find((l) => l.key === course.sampleLessonKey) || ready[0];
}

export function isSampleLesson(course, lessonKey) {
  const sample = sampleLesson(course);
  return !!sample && sample.key === lessonKey;
}

export function nextLesson(course, lessonKey) {
  if (!course) return null;
  const i = course.lessons.findIndex((l) => l.key === lessonKey);
  if (i < 0) return null;
  return course.lessons.slice(i + 1).find((l) => l.status === 'ready') || null;
}

// Course progress for one person, from v_portal_course_progress rows.
export function progressFor(course, rows, userId) {
  const ready = readyLessons(course);
  const mine = (rows || []).filter((r) => r.user_id === userId && r.course_key === course?.key);
  const done = new Set(mine.filter((r) => r.status === 'complete').map((r) => r.lesson_key));
  return {
    done,
    doneCount: ready.filter((l) => done.has(l.key)).length,
    total: ready.length,
    isDone: (key) => done.has(key),
  };
}
